import React from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import { useGetHealthQuery } from '../../redux/internalAlertsApiSlice'
import HealthBadge from './HealthBadge'
import { LIVE_POLL_MS, cadenceLabel, channelOf, count, relTime, runSummary, shortTime, useRefetchOnVisible } from './alertUtils'
import { BAR_BG, GREEN, INK, LINE, MUTED, PRIMARY, RED, WARNING, Th, cardSx } from './ui'

const CELL_COLOR = { OK: GREEN, PARTIAL: WARNING, FAILING: RED, NONE: '#DFE3E8' }

/** 14 cells, one per IST day, oldest first. */
function Strip({ days, strip }) {
  const byDay = {}
  ;(strip || []).forEach(c => { byDay[c.date] = c })
  return (
    <Box sx={{ display: 'inline-flex', gap: '3px' }}>
      {days.map(day => {
        const c = byDay[day] || { state: 'NONE', messages: 0, failed: 0 }
        const tip = c.state === 'NONE' ? `${day} · nothing sent` : `${day} · ${count(c.messages)} message${c.messages === 1 ? '' : 's'}${c.failed ? `, ${count(c.failed)} failed` : ''}`
        return (
          <Tooltip key={day} title={tip} arrow>
            <Box sx={{ width: 12, height: 18, borderRadius: '2px', bgcolor: CELL_COLOR[c.state] || CELL_COLOR.NONE, opacity: c.state === 'NONE' ? 0.7 : 1 }} />
          </Tooltip>
        )
      })}
    </Box>
  )
}

/**
 * The page an on-call person opens at 09:45 when nothing arrived: state and one-line reason per alert, worst first,
 * with a 14-day strip of what went out each day. Everything comes from the write-behind health on the definitions
 * plus one small aggregate — nothing here touches the audit DB.
 */
export default function AlertHealthTab({ channel = 'WHATSAPP', onOpen, onShowHistory }) {
  const { data, isFetching, error, refetch } = useGetHealthQuery(undefined, { pollingInterval: LIVE_POLL_MS })
  useRefetchOnVisible(refetch)
  // One board for every channel; this tab shows the rows of the channel selected at the top of the page.
  const rows = (data?.rows || []).filter(r => channelOf(r) === channel)
  const days = data?.days || []
  const wa = channel === 'WHATSAPP'

  if (isFetching && !data) return <Box sx={{ p: 2 }}>{[...Array(8)].map((_, i) => <Skeleton key={i} height={40} sx={{ mb: 0.5 }} />)}</Box>
  if (error) return <Alert severity='error'>{error?.data?.message || error?.message || 'Could not load health.'}</Alert>

  const problems = rows.filter(r => ['FAILING', 'MISSED'].includes(r.state))
  const attention = rows.filter(r => ['PARTIAL', 'LATE'].includes(r.state))

  return (
    <Box sx={cardSx}>
      <Box sx={{ background: BAR_BG, px: 2, py: 1.1, borderBottom: `1px solid ${LINE}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.8rem', color: MUTED }}>
          {problems.length
            ? <Box component='span' sx={{ color: RED, fontWeight: 700 }}>{count(problems.length)} failing or missed</Box>
            : <Box component='span' sx={{ color: GREEN, fontWeight: 700 }}>Nothing failing or missed</Box>}
          {attention.length ? <> · <Box component='span' sx={{ color: WARNING, fontWeight: 700 }}>{count(attention.length)} need attention</Box></> : null}
          {data?.evaluatedAt && <> · evaluated {shortTime(data.evaluatedAt)} IST</>}
        </Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, color: MUTED, fontSize: '0.7rem' }}>
          {[['OK', 'all accepted'], ['PARTIAL', 'some failed'], ['FAILING', 'all failed'], ['NONE', 'nothing sent']].map(([k, l]) => (
            <Box key={k} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: CELL_COLOR[k] }} />{l}</Box>
          ))}
        </Box>
      </Box>

      <TableContainer sx={{ overflowX: 'auto', maxHeight: 640 }}>
        <Table size='small' stickyHeader sx={{ '& td': { whiteSpace: 'nowrap', fontSize: '0.8rem', borderBottom: `1px solid ${LINE}`, verticalAlign: 'top' }, '& td.wrap': { whiteSpace: 'normal', overflowWrap: 'anywhere' } }}>
          <TableHead>
            <TableRow>
              {['Alert', 'Health', 'Why', 'Expected', 'Last run', 'Recipients', `Last ${days.length || 14} days`, ''].map((h, i) => (
                <Th key={i} right={h === 'Recipients'}>{h}</Th>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.templateName} hover onClick={() => onOpen?.(r.templateName)} sx={{ cursor: 'pointer', '&:hover': { background: '#F9FAFB' } }}>
                <TableCell className='wrap' sx={{ minWidth: 220, py: 1.1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', color: r.enabled ? INK : MUTED, lineHeight: 1.25 }}>{r.displayName || r.templateName}</Typography>
                  <Typography sx={{ fontSize: '0.66rem', color: MUTED, fontFamily: 'monospace' }}>{r.templateName}</Typography>
                </TableCell>
                <TableCell><HealthBadge state={r.state} reason={r.reason} /></TableCell>
                <TableCell className='wrap' sx={{ minWidth: 240, maxWidth: 360, color: ['FAILING', 'MISSED'].includes(r.state) ? RED : MUTED }}>
                  <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.35 }}>{r.reason || '—'}</Typography>
                </TableCell>
                <TableCell sx={{ color: r.expected ? INK : MUTED }}>{cadenceLabel(r.expected)}</TableCell>
                <TableCell>
                  {r.lastRun?.at ? (
                    <Box>
                      <Typography sx={{ fontSize: '0.8rem' }} title={`${r.lastRun.at} IST`}>{shortTime(r.lastRun.at)} <Box component='span' sx={{ color: MUTED }}>· {relTime(r.lastRun.at)}</Box></Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: r.lastRun.failed ? RED : MUTED }}>{runSummary(r.lastRun)}</Typography>
                    </Box>
                  ) : <Box component='span' sx={{ color: MUTED }}>—</Box>}
                </TableCell>
                <TableCell align='right' sx={{ fontWeight: 700 }}>{count(r.activeRecipients)}</TableCell>
                <TableCell><Strip days={days} strip={r.strip} /></TableCell>
                <TableCell align='right' onClick={(e) => e.stopPropagation()}>
                  <Tooltip title='Message history for this alert' arrow>
                    <IconButton size='small' onClick={() => onShowHistory?.(r.templateName)} sx={{ color: PRIMARY }}><HistoryOutlinedIcon fontSize='small' /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={8} align='center' sx={{ py: 5, color: MUTED }}>No alert definitions on this channel yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography sx={{ fontSize: '0.68rem', color: MUTED, px: 2, py: 1.25, borderTop: `1px solid ${LINE}` }}>
        Health is re-checked every 5 minutes and this page refreshes on its own. OK: the last run reached everyone, or ran and had nothing to send.
        Pending: due today, not sent yet. Late: sent after its window. Partial: a message failed or a recipient got nothing. Failing: every message was rejected.
        Missed: its window closed with no run. {wa ? 'Test sends are not counted.' : 'Alerts without a schedule are judged on their last send only; "Never sent" means no send has been recorded since this panel started tracking them.'}
      </Typography>
    </Box>
  )
}
